// src/pages/TargetTimeSettings.jsx
import React, { useEffect, useState } from "react";
import { Calendar } from "react-modern-calendar-datepicker";
import "react-modern-calendar-datepicker/lib/DatePicker.css";
import moment from "moment-jalaali";

const API_URL = import.meta.env.VITE_API_URL;

// ❗ FIX #1 — Do NOT use Persian digits globally
moment.loadPersian({ dialect: "persian-modern", usePersianDigits: false });

export default function TargetTimeSettings() {
  const [selectedDate, setSelectedDate] = useState(null);
  const [time, setTime] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Helper to convert English digits to Persian ONLY when displaying date
  const toPersianDigits = (s) =>
    s.replace(/\d/g, (d) => "۰۱۲۳۴۵۶۷۸۹"[d]);

  // Load from server
  useEffect(() => {
    const fetchTarget = async () => {
      try {
        const res = await fetch(`${API_URL}/target_time`, {
          credentials: "include",
        });
        if (!res.ok) throw new Error("خطا در دریافت");

        const data = await res.json();
        const serverTime = data.target_time; // "2025-11-17 14:30:00"

        if (serverTime) {
          const m = moment(serverTime, "YYYY-M-D HH:mm:ss");

          const jalali = m.format("jYYYY/jMM/jDD").split("/");
          setSelectedDate({
            year: Number(jalali[0]),
            month: Number(jalali[1]),
            day: Number(jalali[2]),
          });

          // ❗ FIX #2 — HH:mm MUST be English digits
          setTime(m.format("HH:mm"));
        }
      } catch (err) {
        console.error(err);
        setMessage("خطا در بارگذاری زمان هدف");
      }
    };

    fetchTarget();
  }, []);

  // Save
  const save = async () => {
    if (!selectedDate || !time) {
      setMessage("لطفاً تاریخ و ساعت را کامل انتخاب کنید");
      return;
    }

    setLoading(true);
    setMessage("");

    // jYYYY/jMM/jDD + time → Gregorian
    const jalaliStr = `${selectedDate.year}/${selectedDate.month}/${selectedDate.day}`;
    const fullJalali = `${jalaliStr} ${time}:00`;

    const gregorianMoment = moment(fullJalali, "jYYYY/jMM/jDD HH:mm:ss");
    const gregorianString = gregorianMoment.format("YYYY-MM-DD HH:mm:ss");

    const payload = { target_time: gregorianString };

    try {
      const res = await fetch(`${API_URL}/target_time`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "خطا در ذخیره");
      }

      setSuccess(true);
      setMessage("زمان هدف با موفقیت ذخیره شد");

      setTimeout(() => {
        setMessage("");
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setMessage(err.message || "خطا در ذخیره‌سازی");
    } finally {
      setLoading(false);
    }
  };

  // Persian formatted display (without breaking input)
  const formatDate = (dateObj) => {
    if (!dateObj) return "";
    const m = moment({
      jYear: dateObj.year,
      jMonth: dateObj.month - 1,
      jDate: dateObj.day,
    });

    // ❗ FIX #3 — convert only displayed text to Persian digits
    return toPersianDigits(m.format("jD jMMMM jYYYY"));
  };

  return (
    <div
      dir="rtl"
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: "'Vazirmatn', Tahoma, sans-serif",
      }}
    >
      <div
        style={{
          background: "white",
          padding: 40,
          borderRadius: 16,
          boxShadow: "0 10px 30px rgba(0,0,0,0.12)",
          maxWidth: 460,
          width: "100%",
          transform: success ? "scale(1.02)" : "scale(1)",
          transition: "all 0.3s ease",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            margin: "0 0 32px",
            fontSize: 26,
            color: "#2d3748",
          }}
        >
          تنظیم زمان هدف
        </h2>

        {/* Calendar */}
        <div style={{ marginBottom: 24 }}>
          <label
            style={{
              display: "block",
              marginBottom: 10,
              fontWeight: 600,
              color: "#2d3748",
            }}
          >
            تاریخ هدف (شمسی)
          </label>

          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            inputPlaceholder="انتخاب تاریخ"
            locale="fa"
            calendarClassName="modern-persian-calendar"
          />
        </div>

        {/* Time Selector */}
        <div style={{ marginBottom: 28 }}>
          <label
            style={{
              display: "block",
              marginBottom: 10,
              fontWeight: 600,
              color: "#2d3748",
            }}
          >
            ساعت هدف
          </label>

          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)} // always English
            style={{
              width: "100%",
              padding: "12px 14px",
              fontSize: 18,
              border: "2px solid #e2e8f0",
              borderRadius: 10,
              backgroundColor: "#f8fafc",
              outline: "none",
              textAlign: "center",
              fontFamily: "Vazirmatn",
            }}
          />
        </div>

        {selectedDate && time && (
          <div
            style={{
              textAlign: "center",
              padding: "12px",
              backgroundColor: "#ebf8ff",
              borderRadius: 10,
              marginBottom: 20,
              color: "#1e40af",
              fontWeight: 500,
            }}
          >
            زمان انتخابی: {formatDate(selectedDate)} ساعت{" "}
            {toPersianDigits(time)}
          </div>
        )}

        <button
          onClick={save}
          disabled={loading}
          style={{
            width: "100%",
            padding: 16,
            fontSize: 18,
            fontWeight: 600,
            color: "white",
            background: loading
              ? "#a0aec0"
              : success
              ? "#48bb78"
              : "#3182ce",
            border: "none",
            borderRadius: 12,
            cursor: loading ? "not-allowed" : "pointer",
            boxShadow: success
              ? "0 0 20px rgba(72,187,120,0.4)"
              : "0 4px 15px rgba(49,130,206,0.25)",
          }}
        >
          {loading
            ? "در حال ذخیره..."
            : success
            ? "ذخیره شد!"
            : "ذخیره زمان هدف"}
        </button>

        {message && (
          <div
            style={{
              marginTop: 20,
              padding: "14px 18px",
              borderRadius: 10,
              textAlign: "center",
              backgroundColor: message.includes("خطا")
                ? "#fed7d7"
                : "#d4edda",
              color: message.includes("خطا") ? "#9b2c2c" : "#2d6a4f",
              border: `1px solid ${
                message.includes("خطا") ? "#feb2b2" : "#a3d9b1"
              }`,
            }}
          >
            {message}
          </div>
        )}
      </div>

      <style jsx>{`
        .modern-persian-calendar {
          direction: rtl;
          font-family: "Vazirmatn", sans-serif;
        }
        .modern-persian-calendar * {
          font-family: "Vazirmatn", sans-serif !important;
        }
      `}</style>
    </div>
  );
}
